import { Match, ParentProps, Show, Signal, Switch, createContext, createEffect, createMemo, createSignal, on, onCleanup, onMount } from 'solid-js'
import { audio, blobToBase64, requireContext } from 'common'
import { audioPlayer as api } from 'st-app/api'
import { AudioPlayerContext } from './AudioPlayerContext'
import styles from './st-audio-player.module.css'
import Flexbox from '../st-flexbox/st-flexbox'
import Icon from '../st-icon/st-icon'
import btnStyles from '../st-button/st-button.module.css'
import Button from '../st-button/st-button';
import Modal from '../st-modal/st-modal';
import { showConfirmation } from '../st-confirmation-dialog/st-confirmation-dialog'
import { Dropdown } from '../..'

const AudioPlayerContextJsx = createContext<AudioPlayerContext>();
export const requireAudioPlayerContext = () => {
    return requireContext(AudioPlayerContextJsx);
}

let canRecord = true;
audio.canRecord().then(state => {
    canRecord = state === 'granted' || state === 'prompt';
});

export class AudioPlayerProps {
    src: string = '';
    srcName: string = '';
    identity: string = '';
    showRecordButton?= false;
    showDeleteButton?= false;
    label?: string = '';
    value?: string = '';
};

export default function AudioPlayer(props: ParentProps<AudioPlayerProps>) {
    const context = new AudioPlayerContext(props.srcName, createMemo(() => props.identity));

    createEffect(() => {
        props.identity; // Re-evaluate when navigating to a new record
        context.setSrc(props.src);
        context.setWaveform(new Float32Array([]));
    });

    return (
        <AudioPlayerContextJsx.Provider value={context}>
            <AudioPlayerInternal {...props} />
        </AudioPlayerContextJsx.Provider>
    )
}

function AudioPlayerInternal(props: ParentProps<AudioPlayerProps>) {
    const ctx = requireAudioPlayerContext();
    let audioEl!: HTMLAudioElement;

    const [showRecordModal, setShowRecordModal] = createSignal(false);
    const [canEdit, setCanEdit] = createSignal(false);
    const [statusMessage, setStatusMessage] = createSignal('');

    if (ctx.srcName && ctx.identity()) {
        api.CanEdit(ctx.srcName, ctx.identity()).then(setCanEdit);
    }

    const labelText = (): string => props.label || 'recording';
    const playLabel = (): string => `Play ${labelText()}`;
    const recordLabel = (): string => `Record ${labelText()}`;
    const deleteLabel = (): string => `Delete ${labelText()}`;

    createEffect(on(ctx.status, (status, prev) => {
        if (prev === undefined) return;
        if (status === 'playing') setStatusMessage('Playing');
        else if (status === 'recording') setStatusMessage('Recording');
        else if (status === 'stopped') setStatusMessage('Stopped');
        else if (status === 'paused') setStatusMessage('Paused');
    }));

    function play() {
        if (ctx.src()) {
            audioEl.play();
            ctx.setStatus('playing');
        }
    }

    //function pause() {
    //    audioEl.pause();
    //    ctx.setStatus('paused');
    //}

    function stop() {
        ctx.setStatus('stopped');
    }

    function removeAudio() {
        showConfirmation('Delete Recording', 'This operation cannot be undone. Delete this recording?')
            .then(() => {
                api.DeleteAudio(ctx.srcName, ctx.identity());
                ctx.setSrc('');
            });
    }

    createEffect(() => {
        if (ctx.status() === 'stopped') {
            audioEl.pause();
            audioEl.currentTime = 0;
        }
    });

    createEffect(on(
        ctx.status,
        (status, prevStatus) => {
            if (status === 'stopped' && prevStatus === 'recording') {
                ctx.settingSrc = new Promise(async (resolve, reject) => {
                    const data = await audio.stopRecording();
                    const base64Data = await blobToBase64(data);
                    const truncData = await api.TrimSilence(base64Data);

                    ctx.setSrc('data:audio/mpeg;base64,' + truncData);
                    resolve();
                });
            }
        }
    ));

    createEffect(() => {
        const base64Data = ctx.src();
        if (base64Data && base64Data.startsWith('data:')) {
            api.ReadSamples(base64Data).then(samples => ctx.setWaveform(new Float32Array(samples)));
        }
    });

    let progTimer: NodeJS.Timeout;
    createEffect(() => {
        clearInterval(progTimer);
        if (ctx.status() === 'playing') {
            progTimer = setInterval(() => {
                if (audioEl.duration > 0) {
                    ctx.setProgress((audioEl.currentTime / audioEl.duration) * 100);
                }
            }, 50);
        }
    });

    onMount(() => {
        if (audioEl) {
            audioEl.addEventListener('ended', stop);

            onCleanup(() => {
                audioEl.removeEventListener('ended', stop);
            });

            createEffect(() => {
                if (!ctx.src()) {
                    audioEl.load();
                }
                else {
                    audioEl.load();
                    audioEl.currentTime = 0;
                }
            });
        }
    });

    const showProgress = createMemo(() => {
        const prog = ctx.progress();
        const status = ctx.status();

        return status === 'recording' || (status === 'playing' && prog > 0);
    });

    const showDeleteButton = createMemo(() => {
        const src = ctx.src();
        const show = props.showDeleteButton;
        const status = ctx.status();

        return status === 'stopped' && show && !!src && canEdit();
    });

    function startRecording() {
        if (ctx.allowRecordModal) {
            setShowRecordModal(true);
        }
        else {
            ctx.setStatus('recording');
        }
    }

    return <Flexbox dir="horizontal" middle class={styles['st-audio-player']}>
        <audio ref={audioEl} preload="none" style="display: none">
            <source src={ctx.src()} type="audio/mp3" />
        </audio>
        <span class="sr-only" aria-live="polite" aria-atomic="true">{statusMessage()}</span>
        <Show when={!!(props.children ?? props.value)}>
            <div class={styles.tracker} style={`left: ${ctx.progress()}%; display: ${showProgress() ? '' : 'none'}`}></div>
        </Show>
        <Show when={ctx.status() !== 'playing' && ctx.status() !== 'recording'}>
            <Show when={ctx.src()}>
                <Flexbox.Item as={Button} class={btnStyles.btn} style="padding: 2px 3px" type="link" tooltip={playLabel()} aria-label={playLabel()} onClick={play} >
                    <Icon src="fa fa-volume" />
                </Flexbox.Item>
            </Show>
            <Show when={!ctx.src()}>
                <Flexbox.Item as={Button} class={btnStyles.btn} style="padding: 2px 3px" type="link" tooltip="No Recording" aria-label={`No ${labelText()}`} onClick={play} >
                    <Icon src="fa fa-volume-xmark" />
                </Flexbox.Item>
            </Show>
        </Show>
        {/*<Show when={ctx.status() === 'playing' }>*/}
        {/*    <Flexbox.Item as={Button} class={btnStyles.btn} style="padding: 2px 3px" type="link" tooltip="Pause" onClick={pause} >*/}
        {/*        <Icon src="fa fa-pause" />*/}
        {/*    </Flexbox.Item>*/}
        {/*</Show>*/}
        <Show when={ctx.status() !== 'stopped'}>
            <Flexbox.Item as={Button} class={btnStyles.btn} style="padding: 2px 3px" type="link" tooltip="Stop" aria-label="Stop" onClick={stop} >
                <Icon src="fa fa-stop" />
            </Flexbox.Item>
        </Show>
        <Show when={!(props.children ?? props.value)}>
            <Flexbox.Item auto style="position: relative; min-height: 28px; min-width: 150px">
                <div class={styles.tracker} style={`left: ${ctx.progress()}%; display: ${showProgress() ? '' : 'none'}`}></div>
                <Timeline />
            </Flexbox.Item>
        </Show>
        <Show when={(props.children ?? props.value)}>
            <Flexbox.Item class="value">
                {props.children ?? props.value}
            </Flexbox.Item>
        </Show>
        <Show when={ctx.status() === 'stopped' && props.showRecordButton && canRecord && canEdit()}>
            <Dropdown open={[showRecordModal, setShowRecordModal]}>
                <Dropdown.Trigger as={Button} class={`${btnStyles.btn} ${styles.action}`} type="link" tooltip={recordLabel()} aria-label={recordLabel()} onClick={startRecording}>
                    <Icon src="fa fa-microphone" />
                </Dropdown.Trigger>

                <Show when={ctx.allowRecordModal}>
                    <Dropdown.Content>
                        <AudioRecordDropdown show={[showRecordModal, setShowRecordModal]} />
                    </Dropdown.Content>
                </Show>
            </Dropdown>
        </Show>
        <Show when={showDeleteButton()}>
            <Flexbox.Item as={Button} class={`${btnStyles.btn} ${styles.action}`} type="link" tooltip={deleteLabel()} aria-label={deleteLabel()} onClick={removeAudio} >
                <Icon src="fa fa-times" />
            </Flexbox.Item>
        </Show>
    </Flexbox>
}

AudioPlayer.defaultProps = new AudioPlayerProps();

function AudioRecordDropdown(props: { show: Signal<boolean> }) {
    const [showRecordModal, setShowRecordModal] = props.show;
    const [timeout, setTimeout] = createSignal(0);

    const ctx = requireAudioPlayerContext();
    const newAudioContext = new AudioPlayerContext(ctx.srcName, ctx.identity);
    const counterVisibility = createMemo(() => newAudioContext.status() === 'recording' ? '' : 'none');
    const [showInitialButton, setShowInitialButton] = createSignal(true);

    newAudioContext.allowRecordModal = false;

    let timer: NodeJS.Timeout;
    let trackTimer: NodeJS.Timeout;
    createEffect(() => {
        clearInterval(timer);
        clearInterval(trackTimer);

        if (newAudioContext.status() === 'recording') {
            newAudioContext.setSrc('');
            newAudioContext.setWaveform(new Float32Array([]));

            audio.startRecording()
                .then(renderer => {
                    newAudioContext.recordRenderer = renderer;
                });

            setTimeout(10);

            timer = setInterval(() => {
                setTimeout(timeout() - 1);
                if (timeout() <= 0) {
                    ctx.setStatus('stopped');
                    newAudioContext.setStatus('stopped');
                }
            }, 1000);

            const startTime = new Date().getTime();
            trackTimer = setInterval(() => {
                const ms = new Date().getTime() - startTime;
                newAudioContext.setProgress((ms / 10000) * 100);
            }, 50);
        }
        else {
            newAudioContext.recordRenderer = undefined;
        }
    });

    createEffect(() => {
        if (newAudioContext.status() === 'stopped') {
            setTimeout(10);
        }
    });

    createEffect(() => {
        if (!showRecordModal()) {
            ctx.setStatus('stopped');
            newAudioContext.setStatus('stopped');
            setShowInitialButton(true);

            clearInterval(timer);
            clearInterval(trackTimer);
        }
        else {
            newAudioContext.recordRenderer = undefined;
        }
    });

    createEffect(() => {
        if (newAudioContext.status() === 'recording') {
            setShowInitialButton(false);
        }
    });

    function saveRecording() {
        ctx.setStatus('stopped');
        newAudioContext.setStatus('stopped');

        // give the signal handlers time to process
        requestAnimationFrame(() => {
            (newAudioContext.settingSrc ?? Promise.resolve()).then(() => {
                if (newAudioContext.src()) {
                    ctx.setSrc(newAudioContext.src());
                    api.SaveAudio(ctx.srcName, ctx.identity(), newAudioContext.src());
                }

                setShowRecordModal(false);
            });
        });
    }

    let startButton!: HTMLButtonElement;
    onMount(() => {
        queueMicrotask(() => startButton?.focus());
    });

    return <Flexbox dir="vertical" middle>
        <Switch>
            <Match when={showInitialButton()}>
                <Button ref={startButton} type="primary" onClick={() => newAudioContext.setStatus('recording')} style="margin: 10px">
                    Start Recording
                </Button>
            </Match>
            <Match when={!showInitialButton()}>
                <Flexbox dir="horizontal" class={styles.sticky} middle>
                    <AudioPlayerContextJsx.Provider value={newAudioContext}>
                        <AudioPlayerInternal srcName={ctx.srcName} identity={ctx.identity()} showRecordButton src="" />
                    </AudioPlayerContextJsx.Provider>
                    <Flexbox.Item class={styles.counter} style={`padding-left: 5px; display: ${counterVisibility()}`} >
                        {timeout()}
                    </Flexbox.Item>
                    <Flexbox.Item>
                        <Button type="link" onClick={saveRecording} tooltip="Save">
                            <Icon src="far fa-check-circle" />
                        </Button>
                    </Flexbox.Item>
                </Flexbox>
            </Match>
        </Switch>
    </Flexbox>
}

function Timeline() {
    const ctx = requireAudioPlayerContext();
    let canvas!: HTMLCanvasElement;

    let recTimer: NodeJS.Timeout;
    onMount(() => {
        createEffect(() => {
            audio.renderWaveform(canvas, ctx.waveform());
        });

        createEffect(() => {
            if (ctx.status() === 'recording') {
                clearInterval(recTimer);
                recTimer = setInterval(() => {
                    ctx.recordRenderer?.draw(canvas);
                }, 50);
            }
            else {
                clearInterval(recTimer);
            }
        });
    });

    return <div class={styles.timeline}>
        <canvas ref={canvas} width={120} height={24} />
    </div>
}
